import dynamic from 'next/dynamic';
import React, { PropsWithChildren } from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';
import {
  TermsAndConditionsConsent_consentSettings$data,
  TermsAndConditionsConsent_consentSettings$key,
} from './__generated__/TermsAndConditionsConsent_consentSettings.graphql';
import { TermsAndConditionsConsentMutation } from './__generated__/TermsAndConditionsConsentMutation.graphql';

export type Props = PropsWithChildren<{
  consentSettingsRef: TermsAndConditionsConsent_consentSettings$key | null;
}>;

const sharedConsentFragment = graphql`
  fragment TermsAndConditionsConsent_consentSettings on ConsentSettingsConnection {
    currentVersion
    consentedVersion
  }
`;

const hasSeenUpdateMutation = graphql`
  mutation TermsAndConditionsConsentMutation {
    updateConsentSettings {
      ...TermsAndConditionsConsent_consentSettings
    }
  }
`;

const TermsAndConditionsModal = dynamic(
  () => import('components/molecules/TermsAndConditionsModal/TermsAndConditionsModal'),
  { ssr: false },
);

export default function TermsAndConditionsConsent({
  children,
  consentSettingsRef,
}: Props): React.ReactElement {
  const consentSettings = useFragment(
    sharedConsentFragment,
    consentSettingsRef,
  ) as TermsAndConditionsConsent_consentSettings$data | null;
  const [updateConsentSettings] =
    useMutation<TermsAndConditionsConsentMutation>(hasSeenUpdateMutation);
  const hasNewTerms =
    (consentSettings?.consentedVersion ?? '') !== (consentSettings?.currentVersion ?? '');

  function handleClose() {
    updateConsentSettings({
      variables: {},
      optimisticUpdater(store) {
        store
          .getRoot()
          .getLinkedRecord('consentSettings')
          ?.setValue(consentSettings?.currentVersion ?? null, 'consentedVersion');
      },
      updater(store) {
        const payload = store.getRootField('updateConsentSettings');
        if (!payload) {
          return;
        }
        const consentSettingsRecord = store.getRoot().getLinkedRecord('consentSettings');
        if (!consentSettingsRecord) {
          return;
        }
        const newConsentedVersion = payload.getValue('consentedVersion');
        consentSettingsRecord.setValue(newConsentedVersion, 'consentedVersion');
      },
    });
  }

  return (
    <>
      {hasNewTerms && <TermsAndConditionsModal onClose={handleClose} />}
      {children}
    </>
  );
}
